import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { ChainNode, ChainPath, ProductChainPickerProps } from './types';
import {
  EMPTY_PATHS,
  expandNavigation,
  nodeKey,
  optionsAtDepth,
  navigationPathFromResults,
  preservesNavigation,
  resolveTargetPath,
  targetKey,
} from './navigation';

type NavigationOptions = Pick<
  ProductChainPickerProps,
  'value' | 'path' | 'paths' | 'selectableKinds' | 'onChange' | 'disabled'
>;

type KeyboardFocus = { depth: number; key: string } | null;

type BrowsedBranch = {
  path: ChainPath;
  /** Selection under which the branch was opened. */
  originValueKey: string;
  /** Selection emitted for this branch and not yet acknowledged by the caller. */
  expectedValueKey?: string;
};

export function useProductChainNavigation({
  value = null,
  path,
  paths = EMPTY_PATHS,
  selectableKinds = ['product'],
  onChange,
  disabled = false,
}: NavigationOptions) {
  const pathInResults = useMemo(() => navigationPathFromResults(value, paths), [value, paths]);
  const resolvedAncestry = useMemo(() => resolveTargetPath(value, path), [value, path]);
  const selectedPath = pathInResults.length > 0 ? pathInResults : resolvedAncestry;
  const valueKey = targetKey(value);

  const containerRef = useRef<HTMLDivElement>(null);
  const lastScrolledValueRef = useRef<string | null>(null);
  const [browsed, setBrowsed] = useState<BrowsedBranch | null>(null);
  const [keyboardFocus, setKeyboardFocus] = useState<KeyboardFocus>(null);

  const browsedApplies = browsed?.originValueKey === valueKey || browsed?.expectedValueKey === valueKey;
  const browsedPath = browsedApplies ? preservesNavigation(browsed.path, paths) : null;
  const navigation = browsedPath ?? (selectedPath.length > 0 ? selectedPath : (paths[0] ?? EMPTY_PATHS));

  // A caller acknowledging our selection keeps the open branch. Any other
  // controlled value change discards it. Navigation already falls back in
  // the render where the value changes; this effect only cleans up the intent.
  useEffect(() => {
    if (!browsed) return;
    if (browsed.expectedValueKey === valueKey) {
      setBrowsed({ path: browsed.path, originValueKey: valueKey });
    } else if (browsed.originValueKey !== valueKey) {
      setBrowsed(null);
    }
  }, [browsed, valueKey]);

  const scrollNavigatedIntoView = useCallback((): boolean => {
    const navigated = containerRef.current?.querySelectorAll('[data-navigated="true"]');
    if (!navigated || navigated.length === 0) return false;
    navigated?.forEach((node) => node.scrollIntoView?.({ block: 'nearest' }));
    return true;
  }, []);

  useEffect(() => {
    if (selectedPath.length === 0 || lastScrolledValueRef.current === valueKey) return;
    if (scrollNavigatedIntoView()) lastScrolledValueRef.current = valueKey;
  }, [scrollNavigatedIntoView, selectedPath, valueKey]);

  const focusOption = useCallback((depth: number, key: string) => {
    const el = containerRef.current?.querySelector<HTMLButtonElement>(
      `[role="option"][data-depth="${depth}"][data-key="${key}"]`,
    );
    el?.focus();
    el?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  const selectNode = useCallback(
    (node: ChainNode, depth: number) => {
      if (disabled) return;
      const nextNavigation = expandNavigation(paths, navigation, depth, node);
      const selectable = selectableKinds.includes(node.kind);
      setBrowsed({
        path: nextNavigation,
        originValueKey: valueKey,
        expectedValueKey: selectable ? nodeKey(node) : undefined,
      });
      if (selectable) onChange?.({ kind: node.kind, id: node.id });
    },
    [disabled, paths, navigation, selectableKinds, onChange, valueKey],
  );

  const handleOptionFocus = useCallback(
    (node: ChainNode, depth: number) => {
      setKeyboardFocus({ depth, key: nodeKey(node) });
      const nextNavigation = expandNavigation(paths, navigation, depth, node);
      setBrowsed({ path: nextNavigation, originValueKey: valueKey });
    },
    [paths, navigation, valueKey],
  );

  const handleOptionKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, depth: number, options: readonly ChainNode[], index: number) => {
      const moveWithinColumn = (delta: number) => {
        const next = options[index + delta];
        if (next) focusOption(depth, nodeKey(next));
      };

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveWithinColumn(1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveWithinColumn(-1);
          return;
        case 'Home':
          event.preventDefault();
          if (options[0]) focusOption(depth, nodeKey(options[0]));
          return;
        case 'End':
          event.preventDefault();
          if (options.length > 0) focusOption(depth, nodeKey(options[options.length - 1]));
          return;
        case 'ArrowRight':
        case 'ArrowLeft':
          break;
        default:
          return;
      }

      event.preventDefault();
      const rendered = Array.from(
        containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"][data-depth]') ?? [],
      );
      const depths = [...new Set(rendered.map((el) => Number(el.dataset.depth)))].sort((a, b) => a - b);
      const nextDepth = depths[depths.indexOf(depth) + (event.key === 'ArrowRight' ? 1 : -1)];
      if (nextDepth === undefined) return;
      const columnButtons = rendered.filter((el) => Number(el.dataset.depth) === nextDepth);
      const navNode = navigation[nextDepth];
      const navKey = navNode ? nodeKey(navNode) : null;
      const next =
        (navKey && columnButtons.find((el) => el.dataset.key === navKey)) ||
        columnButtons[Math.min(index, columnButtons.length - 1)] ||
        columnButtons[0];
      if (next) {
        next.focus();
        next.scrollIntoView?.({ block: 'nearest' });
      }
    },
    [focusOption, navigation],
  );

  const columnCount = useMemo(
    () => paths.reduce((maximum, candidate) => Math.max(maximum, candidate.length), 0),
    [paths],
  );

  return {
    navigation,
    keyboardFocus,
    containerRef,
    columnCount,
    optionsAtDepth: (depth: number) => optionsAtDepth(paths, navigation, depth),
    selectNode,
    handleOptionFocus,
    handleOptionKeyDown,
  };
}
